import { useHistory } from 'react-router';
import { getColor } from 'common/constants/_colors';
import {
  sortObjectKeysBySortingArray,
  calculatePercentage
} from 'common/utils/utils';
import AppRouter from 'common/AppRouter';
import { ISSUE_STATES } from 'views/root/views/home/views/pulse/pulse.common';
import PieChartComponent from 'components/PieChart';
import Text from 'components/base/Text';
import { Flex, LegendSymbol, Padding } from 'components/base/layout-components';
import { DeliveryProgressTooltip } from './DeliveryProgress';
import _ from 'lodash';
import { Tooltip } from '@material-ui/core';
import WidgetDescription from '../../../performance/views/CustomReport/Widgets/CustomWidget/Header/WidgetDescription';
import { LBDeliveryIcon } from '@linearb/buzz';
import { ITERATIONS_TYPES } from 'state/selectors/iterations-picker';
import { TEAM_SPACE_TABS } from '../../../pulse/team-space/team-space.consts';
import { useSelector } from 'react-redux';
import { selectedTeamSelector } from 'state/selectors/ui-state';

const issuesSortForPie = [
  ISSUE_STATES.DONE,
  ISSUE_STATES.IN_PROGRESS,
  ISSUE_STATES.INITIATED
];

const ISSUES_MAP = {
  [ISSUE_STATES.IN_PROGRESS]: {
    title: 'In progress',
    colorName: 'melrose'
  },
  [ISSUE_STATES.INITIATED]: {
    title: 'Not started',
    colorName: 'botticelli'
  },
  [ISSUE_STATES.DONE]: {
    title: 'Done',
    colorName: 'pastelGreen'
  }
};

const getDeliveryProgress = issues => {
  const deliveryProgressSorted = sortObjectKeysBySortingArray(
    issues,
    issuesSortForPie
  );
  const issuesTotal = _.sum(Object.values(issues));
  const doneCount = _.get(issues, ISSUE_STATES.DONE, 0);
  const donePercent = calculatePercentage(doneCount, issuesTotal);

  const deliveryProgress = Object.entries(deliveryProgressSorted)
    .map(([key, value]) => {
      if (ISSUES_MAP[key]) {
        return {
          issueState: key,
          name: ISSUES_MAP[key].title,
          value,
          color: getColor(ISSUES_MAP[key].colorName),
          percent: calculatePercentage(value, issuesTotal)
        };
      }
    })
    .filter(Boolean);

  return { deliveryProgress, donePercent };
};

const COLORS = [
  '#AC98FF',
  '#5CEB73',
  '#3B5998',
  '#5467FF',
  '#812AC1',
  '#812AC1'
];

export const getDeliveryKanbanProgress = issues => {
  const issuesProviderStateKeys = _.groupBy(issues, 'provider_state');

  const kanbanData = _.map(
    issuesProviderStateKeys,
    (issues, issueProviderState) => {
      return {
        name: issueProviderState,
        value: _.size(issues)
      };
    }
  );
  const kanbanDataTop5 = _.orderBy(kanbanData, 'value', 'desc').slice(0, 5);

  return kanbanDataTop5.map((item, index) => {
    return {
      ...item,
      color: COLORS[index]
    };
  });
};

export const DeliveryProgrssChart = ({
  deliveryProgressData,
  donePercent,
  showTooltip,
  showPercent,
  content
}) => {
  const history = useHistory();
  const selectedTeam = useSelector(selectedTeamSelector);

  return (
    <Flex items='end'>
      <Flex>
        <PieChartComponent
          showTooltip={showTooltip}
          isGauge={true}
          data={deliveryProgressData}
          config={{
            dataKey: 'value',
            dataName: 'name'
          }}
          pieSize={{ width: 160, height: 160 }}
          pieLine={20}
          noStroke={true}
          customTooltip={<DeliveryProgressTooltip />}
          innerTextComponent={() => (
            <LBDeliveryIcon className='size-6 text-slate-400' />
          )}
          onCellClick={pieCell => {
            const cellIssueState = pieCell?.issueState;
            if (!cellIssueState) {
              return null;
            }

            return history.push({
              pathname: AppRouter.PULSE.path,
              search: `issueState=${pieCell?.issueState}`,
              state: {
                defaultTab: TEAM_SPACE_TABS.CURRENT,
                defaultTeamId: selectedTeam?.id
              }
            });
          }}
        />
      </Flex>
      <Flex direction='column' pl='x-medium' minWidth='10rem'>
        {showPercent && (
          <div data-testid='donePercent'>
            <WidgetDescription
              formattedAverage={`${donePercent}%`}
              formattedDescription={content}
            />
          </div>
        )}
      </Flex>
    </Flex>
  );
};

export const DeliveryProgrssKanbanChart = ({
  deliveryProgressData,
  showLegend = true,
  emptyData = false
}) => {
  return (
    <Flex items={emptyData ? 'end' : 'center'}>
      <Flex>
        <PieChartComponent
          noStroke
          showTooltip={false}
          data={deliveryProgressData}
          pieSize={{ width: 160, height: 160 }}
          pieLine={20}
          paddingAngle={0}
        />
      </Flex>
      <Flex
        direction='column'
        pl='x-medium'
        minWidth='10rem'
        data-testid='kanban-chart-array'
      >
        {showLegend &&
          !emptyData &&
          deliveryProgressData.map(({ color, name, value }, index) => {
            return (
              <Flex key={`${name}-${index}`} items='center' pb='x-small'>
                <LegendSymbol backgroundColor={color} />
                <Tooltip
                  title={
                    <Text
                      pl='0.5rem'
                      size='small'
                      data-testid={`kanban-chart-child-${index}`}
                    >
                      {value} {name}
                    </Text>
                  }
                >
                  <Padding l='x-small'>
                    <WidgetDescription
                      formattedAverage={value}
                      formattedDescription={name}
                    />
                  </Padding>
                </Tooltip>
              </Flex>
            );
          })}
        {emptyData && (
          <WidgetDescription
            formattedAverage='0'
            formattedDescription='Issues'
          />
        )}
      </Flex>
    </Flex>
  );
};

export const emptyData = [
  {
    color: '#D8DEEA',
    name: 'Not started',
    noCursor: true,
    value: 1,
    percent: 100
  }
];

export const emptyDataKanban = [
  {
    color: '#EBEEF5',
    name: '',
    value: 20
  },
  {
    color: '#BDC1C6',
    name: '',
    value: 15
  },
  {
    color: '#C4CDE0',
    name: '',
    value: 5
  }
];

export const setDeliveryData = (
  issues,
  setDonePercent,
  setDeliveryProgressData,
  iterationType
) => {
  if (iterationType === ITERATIONS_TYPES.SCRUM) {
    const issuesGrouped = _.countBy(issues, 'issue_state');
    const { deliveryProgress, donePercent } =
      getDeliveryProgress(issuesGrouped);
    setDonePercent(donePercent);
    setDeliveryProgressData(deliveryProgress);
  } else {
    const deliveryKanbanPrgogress = getDeliveryKanbanProgress(issues);
    setDeliveryProgressData(deliveryKanbanPrgogress);
  }
};
